import type { LucideIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

/* -----------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */

export interface StepConfig {
  /** Unique identifier for the step */
  id: string;
  /** Display title for the step */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Optional icon for the step */
  icon?: LucideIcon;
}

export type StepState = 'completed' | 'active' | 'pending';

export interface UseStepperOptions {
  /** Array of step configurations */
  steps: StepConfig[];
  /** Initial step index (0-based). Defaults to 0 */
  initialStep?: number;
  /** Callback fired when step changes */
  onStepChange?: (index: number, step: StepConfig) => void;
}

export interface UseStepperReturn {
  // State
  /** Current step index (0-based) */
  activeStep: number;
  /** Current step configuration object */
  currentStep: StepConfig;
  /** The steps array */
  steps: StepConfig[];
  /** Total number of steps */
  totalSteps: number;

  // Derived state
  /** True if on the first step */
  isFirstStep: boolean;
  /** True if on the last step */
  isLastStep: boolean;
  /** True if can proceed to next step (not on last step) */
  canGoNext: boolean;
  /** True if can go back to previous step (not on first step) */
  canGoBack: boolean;

  // Navigation
  /** Go to the next step */
  goNext: () => void;
  /** Go to the previous step */
  goBack: () => void;
  /** Go to a specific step by index */
  goToStep: (stepIndex: number) => void;
  /** Reset to the first step */
  reset: () => void;

  // Helpers
  /** Get the state of a step by index */
  getStepState: (stepIndex: number) => StepState;
}

/* -----------------------------------------------------------------------------
 * Hook Implementation
 * -------------------------------------------------------------------------- */

export function useStepper({
  steps,
  initialStep = 0,
  onStepChange,
}: UseStepperOptions): UseStepperReturn {
  const [activeStep, setActiveStep] = useState(() => {
    // Clamp initial step to valid range
    return Math.max(0, Math.min(initialStep, steps.length - 1));
  });

  const totalSteps = steps.length;

  // Derived state
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === totalSteps - 1;
  const canGoNext = !isLastStep;
  const canGoBack = !isFirstStep;
  const currentStep = steps[activeStep];

  // Navigation functions
  const goToStep = useCallback(
    (stepIndex: number) => {
      const clampedIndex = Math.max(0, Math.min(stepIndex, totalSteps - 1));
      if (clampedIndex !== activeStep) {
        setActiveStep(clampedIndex);
        onStepChange?.(clampedIndex, steps[clampedIndex]);
      }
    },
    [activeStep, totalSteps, steps, onStepChange],
  );

  const goNext = useCallback(() => {
    if (canGoNext) {
      goToStep(activeStep + 1);
    }
  }, [canGoNext, activeStep, goToStep]);

  const goBack = useCallback(() => {
    if (canGoBack) {
      goToStep(activeStep - 1);
    }
  }, [canGoBack, activeStep, goToStep]);

  const reset = useCallback(() => {
    goToStep(0);
  }, [goToStep]);

  // Helper functions
  const getStepState = useCallback(
    (stepIndex: number): StepState => {
      if (stepIndex < activeStep) return 'completed';
      if (stepIndex === activeStep) return 'active';
      return 'pending';
    },
    [activeStep],
  );

  return useMemo(
    () => ({
      // State
      activeStep,
      currentStep,
      steps,
      totalSteps,

      // Derived state
      isFirstStep,
      isLastStep,
      canGoNext,
      canGoBack,

      // Navigation
      goNext,
      goBack,
      goToStep,
      reset,

      // Helpers
      getStepState,
    }),
    [
      activeStep,
      currentStep,
      steps,
      totalSteps,
      isFirstStep,
      isLastStep,
      canGoNext,
      canGoBack,
      goNext,
      goBack,
      goToStep,
      reset,
      getStepState,
    ],
  );
}
